import { DataTable, FormGroupHeader, Pagination } from "@/components/shared";
import { getColumns } from "./columns";
import { GetPaginatedConfigAdministratorsQuery, GetPaginatedConfigAdministratorsQueryVariables, GetPaginatedConfigAdministratorsDocument, UserEdge } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { useRef, useState } from "react";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { isManager, isMemberOfMainOrganization } from "@/utils/authentication";
import AddConfigAdminModal from "./AddConfigAdminModal";
import ConfirmRemoveAlertDialog from "./ConfirmRemoveAlertDialog";
import useUserStore from "@/store/shield";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<GetPaginatedConfigAdministratorsQueryVariables>) => void;
}

const ConfigAdminsComponent = () => {

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "modals"]);
  const { userId } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedConfigAdministrators";

  // State
  const [showAddConfigAdminDialog, setShowAddConfigAdminDialog] = useState<boolean>(false);
  const [showRemoveConfigAdminDialog, setShowRemoveConfigAdminDialog] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const canEdit = isManager() && isMemberOfMainOrganization();

  const handleRemoveClick = (userId: number) => {
    setSelectedUserId(userId);
    setShowRemoveConfigAdminDialog(true);
  };

  /**
   * Action buttons for FormGroupHeader
   * Buttons are conditionally rendered based on the current state
   */
  const actionButtons: ActionButtonConfig[] = [
    canEdit ? { text: t("common:buttons.add"), actionFn: () => setShowAddConfigAdminDialog(true), variant: "default" } : null,
  ].filter(Boolean) as ActionButtonConfig[];

  return (
    <div>
      <FormGroupHeader
        className="pr-4 pl-2"
        title={t("configadmins.title")}
        description={canEdit ? t("configadmins.descriptionedit") : t("configadmins.descriptionview")}
        actionButtons={actionButtons}
      />

      {/* Config Admins table */}
      <div className="pr-4 pl-2 select-text">
        <Pagination<UserEdge, GetPaginatedConfigAdministratorsQuery, GetPaginatedConfigAdministratorsQueryVariables>
          ref={paginationRef}
          props={{
            queryDocument: GetPaginatedConfigAdministratorsDocument,
            paginationName,
            className: "mb-4"
          }}>
          {(dataSlice, loading, displayAmount) => {
            const data = dataSlice.map((edge) => edge.node) ?? [];

            return (
              <>
                <div>
                  <DataTable
                    disableRowColour={true}
                    loading={loading}
                    displayAmount={displayAmount as number}
                    data={data}
                    columns={getColumns(intl, t, userId, handleRemoveClick)}
                    className="max-h-[calc(100vh-20rem)]"
                  />
                </div>
              </>
            );
          }}
        </Pagination>
      </div>

      <AddConfigAdminModal
        open={showAddConfigAdminDialog}
        setOpen={setShowAddConfigAdminDialog}
      />

      <ConfirmRemoveAlertDialog
        open={showRemoveConfigAdminDialog}
        onOpenChange={setShowRemoveConfigAdminDialog}
        selectedUserId={selectedUserId as number}
      />
    </div>
  );
};

export default ConfigAdminsComponent;
